import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { QueryClient } from '@tanstack/react-query'
import {
  Persister,
  PersistQueryClientProvider,
} from '@tanstack/react-query-persist-client'

import { QUERY_KEYS } from '~/constants/react-query'
import { hasAuthCookie } from '~/store/cookieStore'
import { customDeserializer, customSerializer } from '~/utils/requests'

let isPersistenceEnabled = false
if (typeof window !== 'undefined') isPersistenceEnabled = true

// no-op persister for environments where persistence is not enabled.
const noopPersister: Persister = {
  persistClient: async () => {},
  restoreClient: async () => undefined,
  removeClient: async () => {},
}

// the default persister for most queries
const basePersister = isPersistenceEnabled
  ? createSyncStoragePersister({
      storage: window.localStorage,
      serialize: customSerializer,
      deserialize: customDeserializer,
    })
  : noopPersister

// this is used to filter out transaction queries beyond the first page
export const persister: Persister = isPersistenceEnabled
  ? {
      persistClient: async (client) => {
        // A throttled write scheduled before a 401 tore the session down would
        // otherwise land after removeClient() and repopulate the expired
        // account's cache. No auth cookie means nothing should be on disk.
        if (!hasAuthCookie()) {
          return basePersister.removeClient()
        }

        const filteredClient = {
          ...client,
          clientState: {
            ...client.clientState,
            queries: client.clientState.queries.map((query) => {
              if (
                query.queryKey.includes(QUERY_KEYS.TOKEN_PRICE) ||
                query.queryKey.includes(QUERY_KEYS.PRICES_VERSION) ||
                query.queryKey.includes(QUERY_KEYS.POST_ONBOARDING_CHECKLIST) ||
                query.queryKey.includes(QUERY_KEYS.ACCOUNT_IMPORT_JOBS) ||
                query.queryKey.includes(QUERY_KEYS.ORG_ACCOUNTING_LOT_RECOMPUTE)
              ) {
                // Don't persist these, for different reasons:
                // - Token prices/balances would render stale values (money) on reload.
                // - The checklist picks welcome state or dashboard; a persisted copy
                //   would flash the previous session's pick.
                // - Import job and lot recompute status only matter while a
                //   progress view polls them live; a persisted copy is stale.
                return {
                  ...query,
                  state: {
                    ...query.state,
                    data: undefined,
                  },
                }
              }

              if (query.queryKey.includes(QUERY_KEYS.TRANSACTIONS)) {
                const state = query.state
                const stateData = state.data as {
                  pages: unknown[]
                  pageParams: unknown[]
                }
                if (stateData?.pages && stateData.pages.length > 0) {
                  return {
                    ...query,
                    state: {
                      ...state,
                      data: {
                        ...stateData,
                        // Keep only the first page of data
                        pages: stateData.pages.slice(0, 1),
                        // Keep only the first page param (which should be null for the first page)
                        pageParams: stateData.pageParams
                          ? stateData.pageParams.slice(0, 1)
                          : [null],
                      },
                    },
                  }
                }
              }

              // Return non-transaction queries unchanged
              return query
            }),
          },
        }

        await basePersister.persistClient(filteredClient)
      },
      restoreClient: basePersister.restoreClient,
      removeClient: basePersister.removeClient,
    }
  : noopPersister

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
})

export const QueryClientProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60,
        buster: process.env.GIT_COMMIT_HASH,
      }}
    >
      {children}
    </PersistQueryClientProvider>
  )
}
